// store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/client';

const TOKEN_KEY = 'auth_token';
const FORCE_PW_KEY = 'forcePasswordChange';

// ─── JWT Decoder ─────────────────────────────────────────────────────────────
const decodeToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(window.atob(base64));
    } catch (e) {
        console.error('Token decode failed:', e);
        return null;
    }
};

// ─── Thunks ──────────────────────────────────────────────────────────────────
export const login = createAsyncThunk(
    'auth/login',
    async ({ username, password }, { rejectWithValue, dispatch }) => {
        try {
            const res = await api.post('/auth/login', { username, password });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: 'Login failed' });
        }
    }
);

export const changePassword = createAsyncThunk(
    'auth/changePassword',
    async ({ oldPassword, newPassword }, { rejectWithValue, getState }) => {
        const userId = getState().auth.userId;
        if (!userId) throw new Error('User ID not found');

        try {
            const res = await api.post('/auth/change-password', {
                userId,
                oldPassword,
                newPassword,
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || { message: 'Change password failed' });
        }
    }
);

// ─── Global Logout Thunk ─────────────────────────────────────────────────────
// Dispatches a 'global' logout action that all slices can listen to
export const performGlobalLogout = createAsyncThunk(
    'auth/performGlobalLogout',
    async (_, { dispatch, getState }) => {
        const previousUserId = getState().auth.userId;

        // Clear auth state first
        dispatch(logout());

        // Return the previous userId so other slices can clear their user-specific data
        return { previousUserId };
    }
);

// ─── Initial State ───────────────────────────────────────────────────────────
const getEmptyState = () => ({
    authenticated: false,
    token: null,
    username: null,
    userId: null,
    roles: [],
    forcePasswordChange: false,
    loading: false,
    error: null,
    sessionMessage: null,
});

const getInitialState = () => {
    if (typeof window === 'undefined') {
        return getEmptyState();
    }

    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return getEmptyState();

    const decoded = decodeToken(token);
    if (!decoded) {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(FORCE_PW_KEY);
        return getEmptyState();
    }

    const forcePasswordChange = sessionStorage.getItem(FORCE_PW_KEY) === 'true';

    return {
        authenticated: !forcePasswordChange,
        token,
        username: decoded.sub || null,
        userId: decoded.userId || null,
        roles: decoded.roles || [],
        forcePasswordChange,
        loading: false,
        error: null,
        sessionMessage: null,
    };
};

const initialState = getInitialState();

// ─── Slice ───────────────────────────────────────────────────────────────────
const authSlice = createSlice({
    name: 'auth',
    initialState,

    reducers: {
        logout(state) {
            Object.assign(state, getEmptyState());
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(FORCE_PW_KEY);
        },

        sessionExpired(state) {
            Object.assign(state, getEmptyState());
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(FORCE_PW_KEY);
            state.sessionMessage = 'You have been logged out. Another device logged into your account.';
        },

        clearError(state) {
            state.error = null;
        },

        clearSessionMessage(state) {
            state.sessionMessage = null;
        },
    },

    extraReducers: (builder) => {
        builder
            // LOGIN
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                console.log('Login Response:', action.payload);
                const { token, forcePasswordChange = false } = action.payload;

                if (!token) {
                    state.error = 'No token received from server';
                    return;
                }

                const decoded = decodeToken(token);
                if (!decoded) {
                    state.error = 'Invalid token received';
                    return;
                }

                state.token = token;
                state.userId = decoded.userId || null;
                state.username = decoded.sub || null;
                state.roles = decoded.roles || [];
                state.forcePasswordChange = forcePasswordChange;
                state.authenticated = !forcePasswordChange;
                state.error = null;
                state.sessionMessage = null;

                sessionStorage.setItem(TOKEN_KEY, token);

                if (forcePasswordChange) {
                    sessionStorage.setItem(FORCE_PW_KEY, 'true');
                } else {
                    sessionStorage.removeItem(FORCE_PW_KEY);
                }
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Login failed';
            })

            // CHANGE PASSWORD
            .addCase(changePassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(changePassword.fulfilled, (state, action) => {
                state.loading = false;
                state.forcePasswordChange = false;
                state.authenticated = true;
                state.error = null;

                // Update token if server returns new one
                if (action.payload?.token) {
                    const newToken = action.payload.token;
                    const decoded = decodeToken(newToken);

                    state.token = newToken;
                    state.userId = decoded?.userId || state.userId;
                    state.username = decoded?.sub || state.username;
                    state.roles = decoded?.roles || state.roles;

                    sessionStorage.setItem(TOKEN_KEY, newToken);
                }

                sessionStorage.removeItem(FORCE_PW_KEY);
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Change password failed';
            })

            // GLOBAL LOGOUT
            .addCase(performGlobalLogout.fulfilled, (state) => {
                // Auth state already cleared synchronously by the logout() reducer,
                // dispatched inside the performGlobalLogout thunk itself.
                // This case exists so other slices/middleware can key off
                // 'auth/performGlobalLogout/fulfilled' if needed.
            });
    },
});

export const { logout, sessionExpired, clearError, clearSessionMessage } = authSlice.actions;
export default authSlice.reducer;